import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { Disc3, Crown, Check, ArrowLeft, Zap, Clock, MessageSquare, Star } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import { Header } from "../components/Header";

// Use current origin for API calls so it works on both preview and custom domain
const API = `${window.location.origin}/api`;

// Animation variants
const fadeUp = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.6, ease: "easeOut" }
};

export default function ProAccessPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [isCheckingSubscription, setIsCheckingSubscription] = useState(false);
  const [existingSubscription, setExistingSubscription] = useState(null);
  const [paypalClientId, setPaypalClientId] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [showPayPal, setShowPayPal] = useState(false);

  // Fetch PayPal config
  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const response = await axios.get(`${API}/config`);
        setPaypalClientId(response.data.paypal_client_id);
      } catch (error) {
        console.error("Failed to fetch config:", error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchConfig();
  }, []);

  // Check if email has existing subscription
  const checkSubscription = async () => {
    if (!email) {
      toast.error("Please enter your email");
      return;
    }
    
    setIsCheckingSubscription(true);
    try {
      const response = await axios.get(`${API}/subscriptions/check?email=${encodeURIComponent(email)}`);
      if (response.data.is_pro) {
        setExistingSubscription(response.data.subscription);
        toast.success("You already have Pro Access!");
      } else {
        setShowPayPal(true);
      }
    } catch (error) {
      console.error("Error checking subscription:", error);
      setShowPayPal(true);
    } finally {
      setIsCheckingSubscription(false);
    }
  };

  // Handle successful subscription
  const handleSubscriptionSuccess = async (data, actions) => {
    try {
      // Create subscription in our database
      await axios.post(`${API}/subscriptions`, {
        email: email,
        paypal_subscription_id: data.subscriptionID,
        paypal_payer_id: data.payerID
      });
      
      toast.success("Welcome to Pro Access! You can now submit up to 5 tracks per month.");
      
      // Redirect to submission form after short delay
      setTimeout(() => {
        navigate("/#submit-form");
      }, 2000);
      
    } catch (error) {
      console.error("Error saving subscription:", error);
      toast.error("Subscription created but there was an error saving. Please contact support.");
    }
  };

  const benefits = [
    { icon: Disc3, text: "Submit up to 5 tracks per month" },
    { icon: Clock, text: "Priority review within 48–72 hours" },
    { icon: MessageSquare, text: "Curated feedback on your track's momentum" },
    { icon: Star, text: "Early visibility to our private industry network" },
    { icon: Zap, text: "Lower commission: 10% vs 15% on placements" }
  ];

  if (isLoading) {
    return (
      <div className="bg-[#09090b] min-h-screen flex items-center justify-center">
        <Disc3 className="w-12 h-12 text-neon animate-spin" />
      </div>
    );
  }

  return (
    <div className="bg-[#09090b] min-h-screen text-white">
      {/* Noise overlay */}
      <div className="fixed inset-0 z-[9999] pointer-events-none opacity-[0.03] mix-blend-overlay bg-[url('https://grainy-gradients.vercel.app/noise.svg')]" />
      
      {/* Header */}
      <Header />

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-6 pt-28 pb-16">
        {existingSubscription ? (
          // Already subscribed view
          <motion.div {...fadeUp} className="text-center">
            <Crown className="w-16 h-16 text-neon mx-auto mb-6" />
            <h1 className="font-heading text-4xl md:text-5xl font-bold uppercase tracking-tight mb-4">
              You're Already Pro
            </h1>
            <p className="text-zinc-400 text-lg mb-8">
              Your Pro Access is active. You can submit up to 5 tracks per month with priority review.
            </p>
            <div className="border border-zinc-800 p-6 max-w-md mx-auto mb-8">
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500 mb-2">Subscription Status</p>
              <p className="text-neon font-bold text-lg uppercase">{existingSubscription.status}</p>
              <p className="text-zinc-500 text-sm mt-2">
                Member since {new Date(existingSubscription.created_at).toLocaleDateString()}
              </p>
            </div>
            <Link to="/#submit-form">
              <Button className="bg-neon text-black hover:bg-white hover:text-black rounded-none uppercase font-bold tracking-wider px-10 py-5 transition-colors duration-300">
                Submit a Track
              </Button>
            </Link>
          </motion.div>
        ) : (
          // Subscription flow
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Left - Benefits */}
            <motion.div {...fadeUp}>
              <div className="flex items-center gap-3 mb-6">
                <Crown className="w-6 h-6 text-neon" />
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-neon">Pro Access</p>
              </div>
              <h1 className="font-heading text-4xl md:text-5xl font-bold uppercase tracking-tight mb-6">
                Priority Access<br />for Artists
              </h1>
              <p className="text-zinc-400 text-lg leading-relaxed mb-8">
                Get faster review, more submissions, and curated insights for your tracks.
              </p>

              <div className="space-y-4 mb-8">
                {benefits.map((item, index) => (
                  <div key={index} className="flex items-center gap-4">
                    <div className="w-10 h-10 border border-zinc-800 flex items-center justify-center">
                      <item.icon className="w-5 h-5 text-neon" />
                    </div>
                    <span className="text-zinc-300">{item.text}</span>
                  </div>
                ))}
              </div>

              <div className="border border-zinc-800 p-6">
                <div className="flex items-baseline gap-2 mb-2">
                  <span className="font-heading text-4xl font-bold text-white">£14.99</span>
                  <span className="text-zinc-500">/month</span>
                </div>
                <p className="text-zinc-500 text-sm">Cancel anytime. No long-term commitment.</p>
              </div>
            </motion.div>

            {/* Right - Payment */}
            <motion.div 
              {...fadeUp}
              transition={{ ...fadeUp.transition, delay: 0.2 }}
            >
              <div className="border border-zinc-800 bg-zinc-900/30 p-8">
                <h2 className="font-heading text-2xl font-bold uppercase tracking-tight mb-6">
                  {showPayPal ? "Complete Payment" : "Get Started"}
                </h2>

                {!showPayPal ? (
                  // Email entry
                  <div className="space-y-6">
                    <div>
                      <label className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500 block mb-3">
                        Your Email
                      </label>
                      <Input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="your@email.com"
                        className="bg-transparent border border-zinc-800 focus:border-neon rounded-none px-4 py-4 text-lg placeholder:text-zinc-600 focus-visible:ring-0 focus-visible:ring-offset-0 w-full text-white"
                        data-testid="pro-email-input"
                      />
                      <p className="text-zinc-600 text-xs mt-2">
                        This email will be linked to your Pro membership
                      </p>
                    </div>

                    <Button
                      onClick={checkSubscription}
                      disabled={isCheckingSubscription || !email}
                      className="w-full bg-neon text-black hover:bg-white hover:text-black rounded-none uppercase font-bold tracking-wider px-6 py-5 transition-colors duration-300 disabled:opacity-50"
                      data-testid="continue-to-payment-btn"
                    >
                      {isCheckingSubscription ? "Checking..." : "Continue to Payment"}
                    </Button>
                  </div>
                ) : (
                  // PayPal buttons
                  <div className="space-y-6">
                    <p className="text-zinc-400 text-sm mb-4">
                      Subscribing as: <span className="text-white font-bold">{email}</span>
                    </p>
                    
                    {paypalClientId ? (
                      <PayPalScriptProvider 
                        options={{ 
                          clientId: paypalClientId,
                          vault: true,
                          intent: "subscription"
                        }}
                      >
                        <PayPalButtons
                          style={{ 
                            layout: "vertical",
                            color: "black",
                            shape: "rect",
                            label: "subscribe"
                          }}
                          createSubscription={(data, actions) => {
                            return actions.subscription.create({
                              plan_id: "P-2TV52414NY789102LNGM7NSQ"
                            });
                          }}
                          onApprove={handleSubscriptionSuccess}
                          onError={(err) => {
                            console.error("PayPal error:", err);
                            toast.error("Payment failed. Please try again.");
                          }}
                        />
                      </PayPalScriptProvider>
                    ) : (
                      <div className="border border-yellow-800 bg-yellow-900/20 p-4">
                        <p className="text-yellow-400 text-sm">
                          PayPal is not configured yet. Please contact support to complete your subscription.
                        </p>
                      </div>
                    )}

                    <button
                      onClick={() => setShowPayPal(false)}
                      className="text-zinc-500 hover:text-white text-sm transition-colors duration-300"
                    >
                      ← Use different email
                    </button>
                  </div>
                )}

                <div className="mt-8 pt-6 border-t border-zinc-800">
                  <p className="text-zinc-600 text-xs">
                    By subscribing, you agree to our terms. Your subscription will automatically renew each month until cancelled.
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </main>
    </div>
  );
}
